import { useEffect, useRef } from 'react';
import { ChevronRight, X } from 'lucide-react';
import { useUi } from '../../stores/ui';
import { getDrill } from './registry';
import { EmptyState } from '../EmptyState';

/** Slide-over host: KPI -> breakdown -> records, driven by the drill stack in the UI store. */
export function DrillPanel() {
  const stack = useUi((s) => s.drillStack);
  const close = useUi((s) => s.closeDrill);
  const popTo = useUi((s) => s.popDrill);
  const closeRef = useRef<HTMLButtonElement>(null);
  const top = stack[stack.length - 1];

  useEffect(() => {
    if (!top) return;
    const prev = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [top, close]);

  if (!top) return null;
  const def = getDrill(top.id);
  return (
    <>
      <div className="sheet-backdrop" onClick={close} />
      <aside className="drill" role="dialog" aria-modal="true" aria-label="Details">
        <div className="drill-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            {stack.length > 1 && (
              <nav className="drill-crumbs" aria-label="Drill path">
                {stack.map((d, i) => {
                  const label = getDrill(d.id)?.crumb(d.params) ?? d.id;
                  const last = i === stack.length - 1;
                  return (
                    <span key={`${d.id}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {last ? (
                        <span className="t-foot">{label}</span>
                      ) : (
                        <button type="button" onClick={() => popTo(i + 1)}>
                          {label}
                        </button>
                      )}
                      {!last && <ChevronRight size={12} />}
                    </span>
                  );
                })}
              </nav>
            )}
            <div className="drill-eyebrow">{def?.crumb(top.params) ?? 'Details'}</div>
          </div>
          <button ref={closeRef} type="button" className="icon-btn" aria-label="Close details" onClick={close}>
            <X size={18} />
          </button>
        </div>
        <div className="drill-body">
          {def ? (
            <def.View key={`${top.id}:${JSON.stringify(top.params)}`} params={top.params} />
          ) : (
            <EmptyState title="Nothing to show" text={`No detail view registered for "${top.id}".`} />
          )}
        </div>
      </aside>
    </>
  );
}
